import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Building2, Loader, User, FileText } from 'lucide-react';
import apiClient from '@/services/api';

interface ApplicationPreview {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  guarantorName?: string | null;
  guarantorEmail?: string | null;
  guarantorPhone?: string | null;
  formData: Record<string, string> | null;
  property?: { name: string; location?: string } | null;
  unit?: { unitNumber: string; building?: { name: string; address?: string } } | null;
}

export default function LandlordReview() {
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<ApplicationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [resultDecision, setResultDecision] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [resultMessage, setResultMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setPreviewError('Invalid link, missing token.');
      return;
    }
    apiClient
      .get(`/tenant-applications/public/landlord-preview/${token}`)
      .then((r) => setPreview(r.data))
      .catch((err) => {
        setPreviewError(
          err?.response?.data?.message ?? 'This link is invalid, expired, or has already been used.'
        );
      });
  }, [token]);

  const submitDecision = async (decision: 'APPROVED' | 'REJECTED') => {
    if (decision === 'REJECTED' && !rejectReason.trim()) return;
    setStatus('submitting');
    try {
      await apiClient.get(`/tenant-applications/public/landlord-decision/${token}`, {
        params: { decision, ...(decision === 'REJECTED' ? { reason: rejectReason } : {}) },
      });
      setResultDecision(decision);
      setResultMessage(
        decision === 'APPROVED'
          ? 'Thank you, the application has been approved. The property manager will be notified.'
          : 'Thank you, the application has been rejected. The property manager will be notified.'
      );
      setStatus('success');
    } catch (err: unknown) {
      setResultMessage(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Unable to record your decision. The link may have expired.'
      );
      setStatus('error');
    }
  };

  const applicantName = preview
    ? [preview.firstName, preview.lastName].filter(Boolean).join(' ') ||
      preview.email ||
      'Applicant'
    : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">Baytarc</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-secondary-200 overflow-hidden">
          {/* Loading preview */}
          {!preview && !previewError && (
            <div className="p-8 text-center">
              <Loader className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-secondary-500">Loading application details…</p>
            </div>
          )}

          {/* Invalid link */}
          {previewError && (
            <div className="p-8 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Unable to Load</h2>
              <p className="text-secondary-600 mb-6">{previewError}</p>
              <Link
                to="/login"
                className="inline-block px-6 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
              >
                Log In to Baytarc
              </Link>
            </div>
          )}

          {/* Decision submitted */}
          {status === 'success' && (
            <div className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                {resultDecision === 'APPROVED' ? 'Application Approved' : 'Application Rejected'}
              </h2>
              <p className="text-secondary-600">{resultMessage}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="p-8 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Unable to Process</h2>
              <p className="text-secondary-600">{resultMessage}</p>
            </div>
          )}

          {/* Application preview + decision buttons */}
          {preview && status === 'idle' && (
            <>
              <div className="px-6 py-4 border-b border-secondary-100 bg-secondary-50">
                <h2 className="font-semibold text-secondary-900">
                  Tenant Application, Pending Your Approval
                </h2>
              </div>

              <div className="p-6 space-y-5">
                {/* Applicant */}
                <div>
                  <p className="text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Applicant
                  </p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {[
                      ['Name', applicantName],
                      ['Email', preview.email],
                      ['Phone', preview.phone],
                      ['Property', preview.property?.name ?? preview.unit?.building?.name],
                      ['Unit', preview.unit?.unitNumber],
                      ['Address', preview.unit?.building?.address ?? preview.property?.location],
                    ]
                      .filter(([, v]) => v)
                      .map(([label, value]) => (
                        <div key={label as string}>
                          <p className="text-xs text-secondary-400">{label}</p>
                          <p className="font-medium text-secondary-800">{value}</p>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Guarantor */}
                {preview.guarantorName && (
                  <div>
                    <p className="text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2">
                      Guarantor
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      {[
                        ['Name', preview.guarantorName],
                        ['Email', preview.guarantorEmail],
                        ['Phone', preview.guarantorPhone],
                      ]
                        .filter(([, v]) => v)
                        .map(([label, value]) => (
                          <div key={label as string}>
                            <p className="text-xs text-secondary-400">{label}</p>
                            <p className="font-medium text-secondary-800">{value}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Form data */}
                {preview.formData && Object.keys(preview.formData).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Application Form
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      {Object.entries(preview.formData).map(([key, value]) => (
                        <div key={key}>
                          <p className="text-xs text-secondary-400 capitalize">
                            {key.replace(/_/g, ' ')}
                          </p>
                          <p className="font-medium text-secondary-800 break-words">
                            {String(value ?? '') || 'N/A'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Decision */}
                <div className="border-t border-secondary-100 pt-4 space-y-3">
                  {!showRejectForm ? (
                    <div className="flex gap-3">
                      <button
                        onClick={() => submitDecision('APPROVED')}
                        disabled={status === 'submitting'}
                        className="flex-1 py-2.5 px-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        Approve Application
                      </button>
                      <button
                        onClick={() => setShowRejectForm(true)}
                        className="flex-1 py-2.5 px-4 bg-red-50 text-red-700 border border-red-200 rounded-xl font-medium hover:bg-red-100 transition-colors"
                      >
                        Reject Application
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Please provide a reason for rejection (required)"
                        rows={3}
                        className="w-full rounded-xl border border-secondary-300 px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => submitDecision('REJECTED')}
                          disabled={!rejectReason.trim() || status === 'submitting'}
                          className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {status === 'submitting' ? 'Submitting…' : 'Confirm Rejection'}
                        </button>
                        <button
                          onClick={() => {
                            setShowRejectForm(false);
                            setRejectReason('');
                          }}
                          className="px-4 py-2.5 border border-secondary-300 rounded-xl text-sm text-secondary-600 hover:bg-secondary-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-secondary-400 text-center">
                    This link expires in 7 days and can only be used once.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
